import { forwardRef } from 'react';

import { styled } from '@spazious/dollhouse-theme';

import { BaseExtendProps, createBase } from '../Base/index.js';

type SliderWrapperElement = HTMLLabelElement;
interface SliderWrapperProps
  extends BaseExtendProps<React.LabelHTMLAttributes<SliderWrapperElement>> {
  fullWidth?: boolean;
}

const SliderWrapper = styled(createBase('label')).withConfig({
  displayName: 'SliderWrapper',
})<SliderWrapperProps>`
  --pure-material-safari-helper1: rgba(var(--pure-material-primary-rgb, 53, 81, 203), 0.04);
  --pure-material-safari-helper2: rgba(var(--pure-material-primary-rgb, 53, 81, 203), 0.12);
  --pure-material-safari-helper3: rgba(var(--pure-material-primary-rgb, 53, 81, 203), 0.16);
  --pure-material-safari-helper4: rgba(var(--pure-material-primary-rgb, 53, 81, 203), 0.24);
  display: inline-block;
  color: rgba(var(--pure-material-onsurface-rgb, 0, 0, 0), 0.87);
  font-family: var(
    --pure-material-font,
    'Roboto',
    'Segoe UI',
    BlinkMacSystemFont,
    system-ui,
    -apple-system
  );
  font-size: 16px;
  ${({ fullWidth }) => fullWidth && 'width: 100%;'}

  /* Input */
  & > input {
    -webkit-appearance: none;
    position: relative;
    display: block;
    margin: 0 0 -36px;
    width: 100%;
    height: 36px;
    background-color: transparent;
    cursor: pointer;
  }

  /* Without Span */
  & > input:last-child {
    position: static;
    margin: 0;
  }

  /* Span */
  & > span {
    display: inline-block;
    margin-bottom: 36px;
  }

  /* Focus */
  & > input:focus {
    outline: none;
  }

  /* Disabled */
  & > input:disabled {
    cursor: default;
    opacity: 0.38;
  }

  & > input:disabled + span {
    color: rgba(var(--pure-material-onsurface-rgb, 0, 0, 0), 0.38);
  }

  /* Webkit | Track */
  & > input::-webkit-slider-runnable-track {
    margin: 17px 0;
    border-radius: 1px;
    width: 100%;
    height: 3px;
    background-color: rgba(var(--pure-material-primary-rgb, 53, 81, 203), 0.24);
  }

  /* Webkit | Thumb */
  & > input::-webkit-slider-thumb {
    appearance: none;
    -webkit-appearance: none;
    border: none;
    border-radius: 50%;
    height: 3px;
    width: 3px;
    background-color: rgb(var(--pure-material-primary-rgb, 53, 81, 203));
    transform: scale(6, 6);
    transition: box-shadow 0.2s;
  }

  /* Webkit | Hover, Focus */
  &:hover > input::-webkit-slider-thumb {
    box-shadow: 0 0 0 2px var(--pure-material-safari-helper1);
  }

  & > input:focus::-webkit-slider-thumb {
    box-shadow: 0 0 0 2px var(--pure-material-safari-helper2);
  }

  &:hover > input:focus::-webkit-slider-thumb {
    box-shadow: 0 0 0 2px var(--pure-material-safari-helper3);
  }

  /* Webkit | Active */
  & > input:active::-webkit-slider-thumb {
    box-shadow: 0 0 0 2px var(--pure-material-safari-helper4) !important;
  }

  /* Webkit | Disabled */
  & > input:disabled::-webkit-slider-runnable-track {
    background-color: rgba(var(--pure-material-onsurface-rgb, 0, 0, 0), 0.38);
  }

  & > input:disabled::-webkit-slider-thumb {
    background-color: rgb(var(--pure-material-onsurface-rgb, 0, 0, 0));
    color: rgb(var(--pure-material-surface-rgb, 255, 255, 255)); /* Safari */
    box-shadow: 0 0 0 1px rgb(var(--pure-material-surface-rgb, 255, 255, 255)) !important;
    transform: scale(4, 4);
  }

  /* Moz | Track */
  & > input::-moz-range-track {
    margin: 17px 0;
    border-radius: 1px;
    width: 100%;
    height: 3px;
    background-color: rgba(var(--pure-material-primary-rgb, 53, 81, 203), 0.24);
  }

  /* Moz | Thumb */
  & > input::-moz-range-thumb {
    appearance: none;
    -moz-appearance: none;
    border: none;
    border-radius: 50%;
    height: 3px;
    width: 3px;
    background-color: rgb(var(--pure-material-primary-rgb, 53, 81, 203));
    transform: scale(6, 6);
    transition: box-shadow 0.2s;
  }

  /* Moz | Progress */
  & > input::-moz-range-progress {
    border-radius: 1px;
    height: 3px;
    background-color: rgb(var(--pure-material-primary-rgb, 53, 81, 203));
  }

  /* Moz | Hover, Focus */
  &:hover > input:hover::-moz-range-thumb {
    box-shadow: 0 0 0 2px rgba(var(--pure-material-primary-rgb, 53, 81, 203), 0.04);
  }

  & > input:focus::-moz-range-thumb {
    box-shadow: 0 0 0 2px rgba(var(--pure-material-primary-rgb, 53, 81, 203), 0.12);
  }

  &:hover > input:focus::-moz-range-thumb {
    box-shadow: 0 0 0 2px rgba(var(--pure-material-primary-rgb, 53, 81, 203), 0.16);
  }

  /* Moz | Active */
  & > input:active::-moz-range-thumb {
    box-shadow: 0 0 0 2px rgba(var(--pure-material-primary-rgb, 53, 81, 203), 0.24) !important;
  }

  /* Moz | Disabled */
  & > input:disabled::-moz-range-track {
    background-color: rgba(var(--pure-material-onsurface-rgb, 0, 0, 0), 0.38);
  }

  & > input:disabled::-moz-range-progress {
    background-color: rgba(var(--pure-material-onsurface-rgb, 0, 0, 0), 0.87);
  }

  & > input:disabled::-moz-range-thumb {
    background-color: rgb(var(--pure-material-onsurface-rgb, 0, 0, 0));
    box-shadow: 0 0 0 1px rgb(var(--pure-material-surface-rgb, 255, 255, 255)) !important;
    transform: scale(4, 4);
  }

  & > input::-moz-focus-outer {
    border: none;
  }

  /* MS | Track */
  & > input::-ms-track {
    box-sizing: border-box;
    margin: 17px 0;
    border: none;
    border-radius: 1px;
    padding: 0 17px;
    width: 100%;
    height: 3px;
    background-color: transparent;
  }

  & > input::-ms-fill-lower {
    border-radius: 1px;
    height: 3px;
    background-color: rgb(var(--pure-material-primary-rgb, 53, 81, 203));
  }

  /* MS | Progress */
  & > input::-ms-fill-upper {
    border-radius: 1px;
    height: 3px;
    background-color: rgba(var(--pure-material-primary-rgb, 53, 81, 203), 0.24);
  }

  /* MS | Thumb */
  & > input::-ms-thumb {
    appearance: none;
    margin: 0 17px;
    border: none;
    border-radius: 50%;
    height: 3px;
    width: 3px;
    background-color: rgb(var(--pure-material-primary-rgb, 53, 81, 203));
    transform: scale(6, 6);
    transition: box-shadow 0.2s;
  }

  /* MS | Hover, Focus */
  &:hover > input::-ms-thumb {
    box-shadow: 0 0 0 2px rgba(var(--pure-material-primary-rgb, 53, 81, 203), 0.04);
  }

  & > input:focus::-ms-thumb {
    box-shadow: 0 0 0 2px rgba(var(--pure-material-primary-rgb, 53, 81, 203), 0.12);
  }

  &:hover > input:focus::-ms-thumb {
    box-shadow: 0 0 0 2px rgba(var(--pure-material-primary-rgb, 53, 81, 203), 0.16);
  }

  /* MS | Active */
  & > input:active::-ms-thumb {
    box-shadow: 0 0 0 2px rgba(var(--pure-material-primary-rgb, 53, 81, 203), 0.24) !important;
  }

  /* MS | Disabled */
  & > input:disabled::-ms-fill-lower {
    background-color: rgba(var(--pure-material-onsurface-rgb, 0, 0, 0), 0.38);
  }

  & > input:disabled::-ms-fill-upper {
    background-color: rgba(var(--pure-material-onsurface-rgb, 0, 0, 0), 0.38);
    opacity: 0.38;
  }

  & > input:disabled::-ms-thumb {
    background-color: rgb(var(--pure-material-onsurface-rgb, 0, 0, 0));
    box-shadow: 0 0 0 1px rgb(var(--pure-material-surface-rgb, 255, 255, 255)) !important;
    transform: scale(4, 4);
  }
`;

export type SliderElement = HTMLInputElement;

export interface SliderProps extends BaseExtendProps<React.InputHTMLAttributes<SliderElement>> {
  fullWidth?: boolean;
}

export const Slider = styled(
  forwardRef<SliderElement, SliderProps>(({ className, fullWidth, ...restProps }, ref) => {
    return (
      <SliderWrapper className={className} fullWidth={fullWidth}>
        <input ref={ref} type="range" {...restProps} />
        <span></span>
      </SliderWrapper>
    );
  })
).withConfig({
  displayName: 'Slider',
})``;
